import { Box } from '@deckee/deck-hand';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import { BaseMapDefinition, MapSettingsTab } from '../mapSettingsConfig';
import BaseMapTab from './BaseMapTab';
import MapSettingsTabs from './MapSettingsTabs';
import OverlaysTab from './OverlaysTab';
import SheetPanelTransition from './SheetPanelTransition';
import WaypointsTab from './WaypointsTab';

interface MapSettingsContainerViewProps {
  baseMapDefinitions: Record<string, BaseMapDefinition>;
  selectedBaseMapLayer: string | null;
  onSelectBaseMap: (_baseMapLayer: string) => void;
  hasSubscriptionFeaturesEnabled: boolean;
  selectedLayerTypes: string[];
  onToggleLayer: (_layerName: string) => void;
}

/** Sits above the panel and only casts a shadow once content runs under it. */
const StickyHeader = styled(Box)<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1;
  transition: box-shadow 150ms ease;
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? 'rgba(0, 0, 0, 0.2) 0px 10px 30px -20px' : 'none'};
`;

/**
 * This sheet sits on 16px side padding rather than `LayoutBody`'s 8px, and
 * drops the default top inset so content sits against the tab bar. Vertical
 * values have to live here: `LayoutBody`'s `padding` shorthand wins over Box
 * `pt` / `pb` props (see GroupDetailView's `pt="0!important"`).
 */
const MapSettingsBody = styled(SheetBody)`
  padding: 0 ${({ theme }) => theme.space[3]}px
    ${({ theme }) => theme.space[4]}px;
`;

const MapSettingsContainerView: FC<MapSettingsContainerViewProps> = ({
  baseMapDefinitions,
  selectedBaseMapLayer,
  onSelectBaseMap,
  hasSubscriptionFeaturesEnabled,
  selectedLayerTypes,
  onToggleLayer,
}) => {
  const [activeTab, setActiveTab] = useState<MapSettingsTab>('base-map');
  const [scrolled, setScrolled] = useState(false);
  const selectedLayerTypesSet = new Set(selectedLayerTypes);

  return (
    <SheetLayout
      heading="Map settings"
      showMenuBar={false}
      showMapOverlay
      slideUpTransition
      // The panel caps its own height so the sheet clears the status bar and
      // notch; this just keeps SheetLayout from capping it first.
      maxViewHeight="100"
    >
      <StickyHeader
        $scrolled={scrolled}
        data-testid="map-settings-header"
        bg="grays.0"
        px="3"
        py="2"
      >
        <MapSettingsTabs activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <SheetPanelTransition
        contentKey={activeTab}
        onScrolledChange={setScrolled}
      >
        <MapSettingsBody>
          {activeTab === 'base-map' && (
            <BaseMapTab
              baseMapDefinitions={baseMapDefinitions}
              selectedBaseMapLayer={selectedBaseMapLayer}
              onSelectBaseMap={onSelectBaseMap}
              hasSubscriptionFeaturesEnabled={hasSubscriptionFeaturesEnabled}
            />
          )}

          {activeTab === 'overlays' && (
            <OverlaysTab
              selectedLayerTypes={selectedLayerTypesSet}
              onToggleLayer={onToggleLayer}
            />
          )}

          {activeTab === 'waypoints' && (
            <WaypointsTab
              selectedLayerTypes={selectedLayerTypesSet}
              onToggleLayer={onToggleLayer}
            />
          )}
        </MapSettingsBody>
      </SheetPanelTransition>
    </SheetLayout>
  );
};

export default MapSettingsContainerView;
